import pako from "pako";
import { decryptData } from "utils";
import * as actions from "store/actions";

function log(msg) {
  console.log(`[BrokerLog] ${msg}`);
}

export default function RPCWebSocket(dispatch, state) {
  let brokerConfig = null;
  let socketRef = null;
  let isConnected = false;
  let reconnectAttempts = 0;
  let allHosts = [];
  let tokenRefreshTimer = null;
  let manualClose = false;

  let isRefreshing = false;
  let isFetchingToken = false;

  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;

  fetchToken(false).catch(err =>
    console.error("Initial token fetch failed", err)
  );

  async function fetchToken(isRefresh) {
    if (isFetchingToken) {
      log("Token fetch already in progress — skipping");
      return;
    }

    isFetchingToken = true;

    try {
      const loginId = decryptData(localStorage.getItem("mirrorId"));
      const mirrorToken = localStorage.getItem("token");

      const params = new URLSearchParams();
      params.append("loginId", loginId);

      const response = await fetch(
        "https://api.idrivemirror.com/api/v1/rpc/token/fetch",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: mirrorToken
          },
          body: params
        }
      );

      const json = await response.json();
      isFetchingToken = false;

      if (json.status !== "OK") {
        console.error("Token API failed");
        return;
      }

      const data = json.data;
      brokerConfig = {
        token: data.access_token,
        proxy_topic: data.proxy_topic,
        broker_server: data.broker_server,
        viewer_machine_id: decryptData(localStorage.getItem("viewerId")),
        access_token_createdAt: data.access_token_createdAt,
        access_token_expiryAt: data.access_token_expiryAt
      };
      dispatch(actions.setRpcSocketDetails(brokerConfig));

      setupTokenRefreshWatcher(data.access_token_expiryAt);

      if (!isRefresh) {
        connect();
      }
    } catch (error) {
      isFetchingToken = false;
      console.error("Token fetch error:", error);
    }
  }

  function connect() {
    if (!brokerConfig) return;

    if (isRefreshing) {
      log("Skipping connect — token refresh in progress");
      return;
    }

    if (socketRef && socketRef.readyState === WebSocket.CONNECTING) {
      log("Socket is connecting — delaying connect()");
      return setTimeout(connect, 200);
    }

    if (socketRef && socketRef.readyState === WebSocket.OPEN) {
      log("Socket already open — skipping connect()");
      return;
    }

    if (socketRef && socketRef.readyState !== WebSocket.CLOSED) {
      log("Socket not fully closed — delaying connect()");
      return setTimeout(connect, 200);
    }

    if (isConnected) {
      log("Already connected!");
      return;
    }

    const {
      broker_server,
      token,
      proxy_topic,
      viewer_machine_id
    } = brokerConfig;

    const qs =
      `username=${proxy_topic}` +
      `&action=connect&token=${token}` +
      `&machine_id=${viewer_machine_id}_S&s=4&gzip_output=yes`;

    const wsURL = `wss://${broker_server}/proxy/remotehosts?${qs}`;

    log(
      `Attempting WebSocket connection (attempt ${reconnectAttempts + 1})...`
    );

    try {
      const ws = new WebSocket(wsURL);
      ws.binaryType = "arraybuffer";

      socketRef = ws;

      ws.onopen = () => {
        isConnected = true;
        reconnectAttempts = 0;
        log("Connected to broker WebSocket");
      };

      ws.onmessage = event => handleMessage(event);

      ws.onclose = () => {
        isConnected = false;
        log("WebSocket closed");
        if (manualClose) {
          log("Manual close — skip reconnect");
          manualClose = false;
          return;
        }
        scheduleReconnect();
      };

      ws.onerror = () => {
        log("WebSocket error");
        try {
          ws.close();
        } catch (e) {}
      };
    } catch (e) {
      console.error("WebSocket init failed", e);
      scheduleReconnect();
    }
  }

  function extractHostFields(host) {
    return {
      host_name: host.host_name || "",
      host_ip: host.host_ip || "",
      host_os: host.host_os || "",
      host_os_version: host.host_os_version || "",
      machine_id: host.machine_id || "",
      proxy: host.proxy || "",
      viewer_ip: host.viewer_ip || "",
      host_status: host.host_status || "",
      username: host.username || ""
    };
  }

  function scheduleReconnect() {
    if (isRefreshing) {
      log("Skip reconnect — refresh in progress");
      return;
    }

    if (reconnectAttempts >= maxReconnectAttempts) {
      log("Max reconnect attempts reached. Stop retrying.");
      return;
    }
    reconnectAttempts++;
    const delay = reconnectDelay * reconnectAttempts;
    log(`Reconnecting in ${delay / 1000}s...`);
    setTimeout(() => connect(), delay);
  }

  function handleMessage(msg) {
    let payload;
    try {
      if (msg.data instanceof ArrayBuffer) {
        payload = pako.inflate(new Uint8Array(msg.data), { to: "string" });
      } else {
        payload = msg.data;
      }
    } catch (err) {
      console.error("Failed to decompress message:", err);
      return;
    }

    try {
      const json = JSON.parse(payload);
      const { type, host_details = [] } = json;
      if (type === "host_details" && Array.isArray(host_details)) {
        allHosts = host_details.map(h => extractHostFields(h));
        dispatch(actions.setRpcMachineStatusVi(allHosts));
      }
      if (type === "incremental_host_details" && Array.isArray(host_details)) {
        const updated = extractHostFields(host_details[0]);
        const index = allHosts.findIndex(
          h => h.machine_id === updated.machine_id
        );
        if (index !== -1) {
          allHosts[index] = { ...allHosts[index], ...updated };
        } else {
          allHosts.push(updated);
        }
        dispatch(actions.setRpcMachineStatusVi(allHosts));
      }
    } catch (error) {
      console.error("Failed to parse message:", error);
    }
  }

  function setupTokenRefreshWatcher(expiryAt) {
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
    }
    const expireTime = Number(expiryAt);
    const now = Date.now();
    const refreshBefore = 60 * 1000;
    let delay = expireTime - now - refreshBefore;
    if (delay < 5000) delay = 5000;
    log("Token expires at: " + new Date(expiryAt));
    log("Scheduling token refresh in " + delay / 1000 + " seconds");
    tokenRefreshTimer = setTimeout(() => {
      refreshWebSocketConnection();
    }, delay);
  }

  async function refreshWebSocketConnection() {
    log("⚠ Token expiring soon — refreshing WebSocket…");
    manualClose = true;
    isRefreshing = true;
    reconnectAttempts = 0;
    if (socketRef) {
      try {
        socketRef.close();
      } catch (err) {}
    }
    isConnected = false;
    await fetchToken(true);
    isRefreshing = false;
    log("✔ Fetched new token, reconnecting WebSocket...");
    connect();
  }

  function getWebSocket() {
    return socketRef;
  }

  function close() {
    manualClose = true;
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
    if (socketRef) {
      try {
        socketRef.close();
      } catch (e) {}
      socketRef = null;
    }
    isConnected = false;
  }

  return { connect, getWebSocket, close };
}
